// Copyright 2024 praveen.konathala
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     https://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
import axios, { AxiosResponse } from 'axios';
import { cleanParams } from 'src/utils/Utils';
type CacheEntry = {
  data: any;
  timestamp: number;
};

const CACHE: Map<string, CacheEntry> = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // Cache duration in milliseconds (5 minutes)

// Helper to check if the cache is valid
const isCacheValid = (key: string): boolean => {
  const entry = CACHE.get(key);
  if (!entry) return false;
  return Date.now() - entry.timestamp < CACHE_DURATION;
};

// Helper to clear all cache
export const clearCache = () => {
  CACHE.clear();
  console.log('Cache cleared');
};
export const fetchFilters = async (
  url: string,
  headers: Record<string, string>,
) => {
  try {
    const cacheKey = `filters:${url}`;
    if (isCacheValid(cacheKey)) {
      console.log(`Using cached data for ${url}`);
      return CACHE.get(cacheKey)?.data;
    }
    const response = await axios.get(url, { headers });
    if (!response.data) {
      throw new Error('Failed to fetch filters');
    }
    const data = await response.data;
    CACHE.set(cacheKey, { data, timestamp: Date.now() });
    return data;
  } catch (error) {
    console.error('API request failed, loading local data.', error);

    throw new Error('something wrong');
  }
};

export const fetchData = async (
  url: string,
  headers: Record<string, string>,
) => {
  try {
    const cacheKey = `fetchData:${url}`;
    if (isCacheValid(cacheKey)) {
      console.log(`Using cached data for ${url}`);
      return CACHE.get(cacheKey)?.data;
    }

    const response = await axios.get(url, { headers });
    if (!response.data) {
      throw new Error('Failed to fetch filters');
    }
    const data = await response.data;
    CACHE.set(cacheKey, { data, timestamp: Date.now() });
    return data;
  } catch (error) {
    console.error('API request failed, loading local data.', error);
    throw new Error('No Data');
  }
};
export const fetchDataWithParams = async (
  url: string,
  headers: Record<string, string>,
  params: Record<string, string>,
) => {
  try {
    const cacheKey = `dataWithParams:${url}:${JSON.stringify(cleanParams(params))}`;
    if (isCacheValid(cacheKey)) {
      console.log(`Using cached data for ${url}`);
      return CACHE.get(cacheKey)?.data;
    }

    const response = await axios.get(url, {
      headers,
      params: cleanParams(params),
    });
    if (!response.data) {
      throw new Error('Failed to fetch filters');
    }
    const data = await response.data;
    CACHE.set(cacheKey, { data, timestamp: Date.now() });
    return data;
  } catch (error) {
    console.error('API request failed, loading local data.', error);

    throw new Error(
      `Failed to fetch data from ${url}: ${error.response?.status || 'Unknown Status'} - ${
        error.response?.data?.message || error.message
      }`,
    );
  }
};

export const fetchDataWithoutParams = async (
  url: string,
  headers: Record<string, string>,
) => {
  try {
    const cacheKey = `dataWithoutParams:${url}`;
    if (isCacheValid(cacheKey)) {
      console.log(`Using cached data for ${url}`);
      return CACHE.get(cacheKey)?.data;
    }

    const response = await axios.get(url, {
      headers,
    });

    if (!response.data) {
      throw new Error('Failed to fetch storedetails');
    }

    const data = await response.data;
    CACHE.set(cacheKey, { data, timestamp: Date.now() });
    return data;
  } catch (error) {
    console.error('API request failed, loading local data.', error);

    throw new Error(
      `Failed to fetch data from ${url}: ${error.response?.status || 'Unknown Status'} - ${
        error.response?.data?.message || error.message
      }`,
    );
  }
};


export const fetchMultipleData = async (requests: any[]) => {
  try {
    const responses = await Promise.all(
      requests.map(async (request: any) => {
        const { url, headers, params } = request;
        const cacheKey = `multiData:${url}:${JSON.stringify(params)}`;

        // Check if valid cache exists
        if (isCacheValid(cacheKey)) {
          console.log(`Using cached data for ${url}`);
          return CACHE.get(cacheKey)?.data;
        }

        // Fetch fresh data if no valid cache or forceRefresh is true
        try{
          const response = await axios.get(url, { headers, params });
          const data = response.data;
  
          // Store the new data in the cache
          CACHE.set(cacheKey, { data, timestamp: Date.now() });
          return data;
        }catch(error){
          return {
            "results" : [{[params.report]: 0, data: []}]
          };
        }
        
        
      }),
    );
    // const data = responses.map((response: any) => response);

    // console.log(data);
    return responses;
  } catch (error) {
    console.error('Error fetching data:', error);
  }
};

export const updateAlertStatusChange = async (
  url: string,
  headers: Record<string, string>,
  params: Record<string, any>, 
) => {
  try {
    const response = await axios.post(url, null, { headers, params });

    if (!response.data) {
      throw new Error('No response data received.');
    }

    return response.data;
  } catch (error: any) {
    console.error('POST API request failed:', error);

    throw new Error(
      `Failed to post data to ${url}: ${error.response?.status || 'Unknown Status'} - ${
        error.response?.data?.message || error.message
      }`,
    );
  }
};
